/**
 * Tracks the user's pointer in viewport (webview-local) coordinates.
 *
 * Two input streams, whichever fires more recently wins:
 *   1. DOM `pointermove`/`mousemove` — fires only while the pointer is
 *      over our window (workspace mode, peek mode).
 *   2. Tauri `cursor-position` event — emitted by the Rust cursor
 *      tracker (~30Hz) with global CG-screen coords. Translated to
 *      webview-local by subtracting the current window origin. Lets the
 *      agent cursor follow the user pointer in compact / overlay mode
 *      where the webview never receives DOM pointer events because the
 *      window is click-through and the OS routes pointer activity to
 *      whatever app is actually beneath the cursor.
 *
 * Module-level state (not React state) because the rAF animation loop
 * reads it imperatively and doesn't need re-renders.
 */
import { listen } from "@tauri-apps/api/event";
import { isTauri } from "@/lib/is-tauri";
import { getWindowOrigin, primeWindowOrigin } from "@/desktop/use-window-origin";

let latestUserPointer: { x: number; y: number } | null = null;
let listenerMounted = false;

// When the user submits a prompt (cursor input bar, main bar, etc.) the
// agent cursor for that turn doesn't spawn until the backend round-trips
// — typically 100-800ms later. During that window the user often moves
// their mouse, and the cursor would otherwise spawn at the *new* pointer
// position, surprising the user. `pinnedUserPointer` snapshots the
// submit-time location for spawn anchoring only. Live-follow animation
// must bypass the pin so the agent tracks the user's real pointer without
// a multi-second sticky delay.
let pinnedUserPointer: { x: number; y: number } | null = null;
let pinnedUntilMs = 0;

function handleMove(event: PointerEvent | MouseEvent): void {
  latestUserPointer = { x: event.clientX, y: event.clientY };
}

/** Mount the global listener once. Idempotent. */
export function ensureUserPointerTracker(): void {
  if (listenerMounted || typeof window === "undefined") return;
  listenerMounted = true;
  window.addEventListener("pointermove", handleMove, { passive: true });
  window.addEventListener("mousemove", handleMove, { passive: true });

  // Tauri-only stream: the Rust cursor tracker emits global cursor
  // position at ~30Hz. Convert to webview-local by subtracting the
  // current window origin so positions match what DOM pointermove
  // would have reported — the agent cursor animation hook treats both
  // sources identically.
  if (isTauri()) {
    primeWindowOrigin();
    void listen<{ x: number; y: number }>("cursor-position", (e) => {
      const o = getWindowOrigin();
      latestUserPointer = {
        x: e.payload.x - o.x,
        y: e.payload.y - o.y,
      };
    });
  }
}

/** Latest known viewport pointer position, or null if the user has not
 *  moved the pointer since page load. While a pin is active (set by
 *  `pinUserPointer`) the pinned position is returned instead — this lets
 *  the agent cursor spawn where the user clicked Submit even if their
 *  mouse has drifted by the time the backend round-trips. */
export function getLatestUserPointer(): { x: number; y: number } | null {
  if (pinnedUserPointer && performance.now() < pinnedUntilMs) {
    return pinnedUserPointer;
  }
  if (pinnedUserPointer && performance.now() >= pinnedUntilMs) {
    pinnedUserPointer = null;
  }
  return latestUserPointer;
}

/** Latest physical pointer position, ignoring submit-time pins. Use this
 *  inside rAF follow loops so the agent cursor keeps up with real movement
 *  immediately after a prompt is submitted. */
export function getLiveUserPointer(): { x: number; y: number } | null {
  return latestUserPointer;
}

/** Pin the pointer to a specific viewport position for `ttlMs`
 *  milliseconds. Used at prompt-submit time so the agent cursor anchors
 *  to the click-the-button position rather than wherever the user has
 *  drifted to by the time the agent spawns its cursor (typically a few
 *  hundred ms later). */
export function pinUserPointer(
  pos: { x: number; y: number },
  ttlMs = 3000,
): void {
  pinnedUserPointer = pos;
  pinnedUntilMs = performance.now() + ttlMs;
}

/** Release any active pin immediately. */
export function clearPinnedUserPointer(): void {
  pinnedUserPointer = null;
  pinnedUntilMs = 0;
}
